'use client';

import Link from 'next/link';
import { ArrowRight } from 'lucide-react';

export interface RelatedLink {
  title: string;
  href: string;
  description?: string;
  category?: string;
}

interface RelatedLinksProps {
  title?: string;
  links: RelatedLink[];
  columns?: 2 | 3 | 4;
  variant?: 'default' | 'compact' | 'cards';
  className?: string;
}

/**
 * RelatedLinks Component
 *
 * Displays related internal links to improve SEO and user navigation.
 * Can be used for related products, guides, room pages, or blog posts.
 *
 * @example
 * <RelatedLinks
 *   title="Related Guides"
 *   links={[
 *     { title: "How to Measure", href: "/guides/measuring", description: "..." },
 *     { title: "Installation Tips", href: "/guides/installation" }
 *   ]}
 *   columns={3}
 * />
 */
export default function RelatedLinks({
  title = 'Related Pages',
  links,
  columns = 3,
  variant = 'default',
  className = '',
}: RelatedLinksProps) {
  if (!links || links.length === 0) return null;

  const gridCols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  };

  // Compact variant - simple list
  if (variant === 'compact') {
    return (
      <div className={`py-8 ${className}`}>
        <h2 className="text-xl font-bold text-gray-900 mb-4">{title}</h2>
        <ul className="space-y-2">
          {links.map((link, index) => (
            <li key={index}>
              <Link
                href={link.href}
                className="text-primary-red hover:text-primary-red-dark inline-flex items-center gap-2 transition-colors group"
              >
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                <span className="underline">{link.title}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Cards variant - full featured cards
  if (variant === 'cards') {
    return (
      <div className={`py-8 ${className}`}>
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">{title}</h2>
        <div className={`grid ${gridCols[columns]} gap-6`}>
          {links.map((link, index) => (
            <Link
              key={index}
              href={link.href}
              className="group block bg-white border border-gray-200 rounded-lg p-6 hover:border-primary-red hover:shadow-lg transition-all duration-200"
            >
              {link.category && (
                <span className="inline-block text-xs font-semibold text-primary-red bg-red-50 px-2 py-1 rounded mb-2">
                  {link.category}
                </span>
              )}
              <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-primary-red transition-colors">
                {link.title}
              </h3>
              {link.description && (
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                  {link.description}
                </p>
              )}
              <span className="inline-flex items-center text-primary-red font-medium text-sm group-hover:gap-2 transition-all">
                Learn More
                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  // Default variant - simple grid with links
  return (
    <div className={`py-8 border-t border-gray-200 ${className}`}>
      <h2 className="text-xl font-bold text-gray-900 mb-6">{title}</h2>
      <div className={`grid ${gridCols[columns]} gap-4`}>
        {links.map((link, index) => (
          <Link
            key={index}
            href={link.href}
            className="group p-4 bg-gray-50 rounded-lg hover:bg-primary-red hover:text-white transition-colors duration-200"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                {link.category && (
                  <span className="text-xs font-semibold text-gray-500 group-hover:text-red-100 mb-1 block">
                    {link.category}
                  </span>
                )}
                <h3 className="font-semibold text-gray-900 group-hover:text-white mb-1">
                  {link.title}
                </h3>
                {link.description && (
                  <p className="text-sm text-gray-600 group-hover:text-red-100 line-clamp-2">
                    {link.description}
                  </p>
                )}
              </div>
              <ArrowRight className="w-5 h-5 flex-shrink-0 text-primary-red group-hover:text-white group-hover:translate-x-1 transition-all" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
